import { type LinkProps, useRouter } from "@tanstack/react-router";
import { type FragmentType, graphql, useFragment } from "@repo/graphql-types";
import {
  CardGroupItem,
  CardGroupLabel,
  CardGroupRoot,
  CardGroupValue,
} from "@/components/card-group";

export const HomeSectionLivanceAssistCardGroupQueryFragment = graphql(`
  fragment HomeSectionLivanceAssistCardGroupQueryFragment on query_root {
    customerContactData: NuntiusGetCustomerContactManyPeriodSummary(
      arg1: { from: $fromStr, to: $toStr }
    ) {
      totalCustomerContacts
      convertedAppointmentsPercent: percentCustomerContactsAppointmentsScheduled
    }
    rescueAppointmentsData: analyticsMembroTotalDeAgendamentosResgatadosPorFluxo(
      arg1: { dataInicio: $fromStr, dataFim: $toStr }
    ) {
      data {
        rescuedAppointments: agendamentosRecuperados
      }
    }
    lembretes: lembretes_aggregate(
      where: {
        ativo: { _eq: true }
        dataParaEnvio: { _gte: $fromDateTime, _lte: $toDateTime }
        dataEnviado: { _is_null: false }
      }
    ) {
      lembretesEnviados: aggregate {
        total: count
      }
    }
  }
`);

interface HomeSectionLivanceAssistCardGroupProps {
  data: FragmentType<typeof HomeSectionLivanceAssistCardGroupQueryFragment>;
  periodDetails: string;
}

export const HomeSectionLivanceAssistCardGroup = ({
  data,
  periodDetails,
}: HomeSectionLivanceAssistCardGroupProps): JSX.Element => {
  const fragmentData = useFragment(HomeSectionLivanceAssistCardGroupQueryFragment, data);

  const rescuedAppointments: number = fragmentData.rescueAppointmentsData?.data[0]
    ? Number(fragmentData.rescueAppointmentsData.data[0].rescuedAppointments)
    : 0;

  const totalCustomerContacts =
    fragmentData.customerContactData?.[0].totalCustomerContacts ?? 0;

  const convertedAppointmentsPercent =
    fragmentData.customerContactData?.[0].convertedAppointmentsPercent ?? 0;

  const remindersSent = fragmentData.lembretes.lembretesEnviados?.total ?? 0;

  const router = useRouter();
  const navigateToPage = async (to: LinkProps["to"]): Promise<void> => {
    if (to) {
      await router.navigate({ to: "/settings/assist", search: { from: "/home" } });
      await router.navigate({ to, search: { from: "/home" } });
    }
  };

  return (
    <>
      <CardGroupRoot>
        <button
          id="assist-card-customer-contact"
          type="button"
          onClick={() => {
            void navigateToPage("/settings/assist/customer-contact-overview");
          }}
        >
          <CardGroupItem className="h-full text-start">
            <CardGroupValue>{totalCustomerContacts}</CardGroupValue>
            <CardGroupLabel>Contatos atendidos</CardGroupLabel>
          </CardGroupItem>
        </button>

        <button
          id="assist-card-converted-appointments"
          type="button"
          onClick={() => {
            void navigateToPage("/settings/assist/customer-contact-overview");
          }}
        >
          <CardGroupItem className="h-full text-start">
            <CardGroupValue>{convertedAppointmentsPercent}%</CardGroupValue>
            <CardGroupLabel>Conversão de agendamentos</CardGroupLabel>
          </CardGroupItem>
        </button>

        <button
          type="button"
          id="assist-card-rescued-appointments"
          onClick={() => {
            void navigateToPage("/settings/assist/rescued-appointment-overview");
          }}
        >
          <CardGroupItem className="h-full text-start">
            <CardGroupValue>{rescuedAppointments}</CardGroupValue>
            <CardGroupLabel>Agendamentos recuperados</CardGroupLabel>
          </CardGroupItem>
        </button>

        <button
          type="button"
          id="assist-card-reminders-sent"
          onClick={() => {
            void navigateToPage("/settings/assist/follow-up-appointment-reminder");
          }}
        >
          <CardGroupItem className="h-full text-start">
            <CardGroupValue>{remindersSent}</CardGroupValue>
            <CardGroupLabel>Lembretes enviados</CardGroupLabel>
          </CardGroupItem>
        </button>
      </CardGroupRoot>

      <p className="text-neutral-500 text-sm">{periodDetails}</p>
    </>
  );
};
